import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { getStorageValue } from '@genfun/core/utils';
import { fetchUser, logPageView, getSubscription } from '@genfun/core/api/auth';
import { SITE } from './data/site';
import { SessionContext } from './utils/session';
import { Layout } from './components/Layout';
import { Video } from './pages/Video';
import { Photo } from './pages/Photo';
import { Terms, Privacy, Compliance2257, Contact } from './pages/Legal';
import { PaymentRules } from './pages/PaymentRules';
import { TakeItDownReport } from './pages/TakeItDownReport';
import { LoginPopup } from './popups/LoginPopup';
import { PricingPopup } from './popups/PricingPopup';
import { AccountPopup } from './popups/AccountPopup';
import { AgeGatePopup } from './popups/AgeGatePopup';

// The token key is shared with @genfun/core (setToken / fetchUser), so it is not site-prefixed.
const TOKEN_KEY = 'igenfuntoken';

const readToken = () => {
    try {
        const saved = localStorage.getItem(TOKEN_KEY);
        return saved !== null ? JSON.parse(saved) : null;
    } catch { return null; }
};

const initialAgeConfirmed = () => {
    try {
        if (process.env.NODE_ENV !== 'production' && new URLSearchParams(window.location.search).has('qa')) return true;
        return localStorage.getItem(TOKEN_KEY) !== null || localStorage.getItem(SITE.storage.age) === 'true';
    } catch { return false; }
};

function AppShell() {
    // Read synchronously so a returning user never sees the "Log in" button flash.
    const [token, setToken] = useState(readToken);
    const [user, setUser] = useState(null);
    const [authResolved, setAuthResolved] = useState(false);
    const [signedIn, setSignedIn] = useState(false);
    const [subStatus, setSubStatus] = useState(null);
    const [showLogin, setShowLogin] = useState(false);
    const [showPricing, setShowPricing] = useState(false);
    const [showAccount, setShowAccount] = useState(false);
    const [ageConfirmed, setAgeConfirmed] = useState(initialAgeConfirmed);

    useEffect(() => { document.title = `${SITE.name} — ${SITE.tagline}`; }, []);

    useEffect(() => { if (user) setAgeConfirmed(true); }, [user]);

    const confirmAge = () => {
        try { localStorage.setItem(SITE.storage.age, 'true'); } catch {}
        setAgeConfirmed(true);
    };

    // Muted autoplay needs a user gesture on iOS / Low Power Mode: resume paused
    // previews on every tap. Videos the user paused on purpose opt out via data-manual.
    useEffect(() => {
        if (!ageConfirmed) return undefined;
        const playPaused = () => {
            document.querySelectorAll('video[autoplay]:not([data-manual])').forEach((v) => {
                if (v.paused) v.play().catch(() => {});
            });
        };
        playPaused();
        document.addEventListener('touchstart', playPaused, { passive: true });
        document.addEventListener('click', playPaused);
        return () => {
            document.removeEventListener('touchstart', playPaused);
            document.removeEventListener('click', playPaused);
        };
    }, [ageConfirmed, user]);

    useEffect(() => {
        let cancelled = false;
        if (readToken()) setAuthResolved(false);
        (async () => {
            const t = getStorageValue(TOKEN_KEY, null, setToken);
            try {
                const [u, status] = await fetchUser(t);
                if (cancelled) return;
                if (status === 401) setToken(null);
                setUser(status === 401 ? null : u);
                let sub = null;
                if (t && status !== 401) {
                    try {
                        const [subData, subCode] = await getSubscription(t);
                        sub = subCode === 200 && subData?.status ? subData.status : null;
                    } catch { sub = null; }
                }
                if (!cancelled) setSubStatus(sub);
            } catch {
                if (!cancelled) { setUser(null); setSubStatus(null); }
            } finally {
                if (!cancelled) setAuthResolved(true);
            }
        })();
        return () => { cancelled = true; };
    }, [signedIn]);

    useEffect(() => {
        const refParam = new URLSearchParams(window.location.search).get('ref');
        if (refParam && !localStorage.getItem('referral_code')) localStorage.setItem('referral_code', refParam);
        logPageView(refParam || localStorage.getItem('referral_code'));
    }, []);

    const refreshUser = useCallback(() => setSignedIn((s) => !s), []);

    const session = useMemo(() => {
        const openLogin = () => setShowLogin(true);
        const openPricing = () => setShowPricing(true);
        return {
            token, user, subStatus, ageConfirmed,
            authPending: !!token && !user && !authResolved,
            // The user arrives before the subscription does; paywall UI waits for both.
            subPending: !!token && !authResolved,
            refreshUser, openLogin, openPricing,
            openAccount: () => setShowAccount(true),
            // Credits pill: lapsed subscribers manage their plan, everyone else sees pricing.
            openBilling: () => (subStatus && subStatus !== 'active' ? setShowAccount(true) : setShowPricing(true)),
            signOut: () => { localStorage.removeItem(TOKEN_KEY); window.location.reload(); },
            // Gate for every generating action: login first, then an active subscription.
            requireSub: (fn) => {
                if (!user) { openLogin(); return false; }
                if (subStatus !== 'active') { openPricing(); return false; }
                fn?.();
                return true;
            },
        };
    }, [token, user, subStatus, ageConfirmed, authResolved, refreshUser]);

    return (
        <SessionContext.Provider value={session}>
            <Routes>
                <Route element={<Layout />}>
                    <Route index element={<Video />} />
                    <Route path="photo" element={<Photo />} />
                    <Route path="terms" element={<Terms />} />
                    <Route path="privacy" element={<Privacy />} />
                    <Route path="2257" element={<Compliance2257 />} />
                    <Route path="contact" element={<Contact />} />
                    <Route path="payment-rules" element={<PaymentRules />} />
                    <Route path="report" element={<TakeItDownReport />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
            </Routes>
            <LoginPopup isOpen={showLogin} onClose={() => setShowLogin(false)} onSignedIn={refreshUser} />
            <PricingPopup isOpen={showPricing} onClose={() => setShowPricing(false)} />
            <AccountPopup isOpen={showAccount} onClose={() => setShowAccount(false)} />
            {!ageConfirmed && <AgeGatePopup onConfirm={confirmAge} />}
        </SessionContext.Provider>
    );
}

function App() {
    const gid = process.env.REACT_APP_GOOGLE_ID || '';
    const shell = (
        <BrowserRouter>
            <AppShell />
        </BrowserRouter>
    );
    return gid ? <GoogleOAuthProvider clientId={gid}>{shell}</GoogleOAuthProvider> : shell;
}

export default App;
